import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { interval, Subject, Subscription, throwError } from 'rxjs';
import { BreadcrumbOption, HeadingComponentConfig, IconType, TimelineItem, SkeletonType } from '@rappider/rappider-components/utils';
import { GitActionWorkflowRunDetailResponseDto, Project, ProjectVersion, ProjectWithRelations } from '@rappider/rappider-sdk';
import { PATH_DEFINITIONS, defaultToolbarTitleHeadingSize } from '@rappider/shared/definitions';
import { titleBarActionButtonsConfig } from './utils/title-bar-action-buttons.config';
import { deployErrorAlertConfig } from './utils/deploy-error-alert.config';
import { GetLivePreviewInformation, } from '../../states/deploy-management/deploy-management.actions';
import { DeployManagementState } from '../../states/deploy-management/deploy-management.reducer';
import { DeployStatus } from './utils/deploy-status.enum';
import { TimelineItemColor } from 'libs/components/src/lib/utils/timeline/timeline-item-color.enum';
import { projectDeploymentAnimation } from './utils/project-deployment-animation';
import * as moment from 'moment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, takeUntil } from 'rxjs/operators';
import { StartApplication, StopApplication } from 'libs/project-source-code/src/lib/project-source-code-data-source/project-source-code.actions';
import { StoreKeys, SyncRemoteDataService } from 'libs/shared/src/lib/services/sync/sync-remote-data.service';
import { isEqual } from 'lodash';
import { ThemeMode } from 'libs/shared/src/lib/models/theme';
import { MonacoEditorTheme } from 'libs/shared/src/lib/configs/monaco-editor-theme';
import { AuthState } from 'libs/authentication/src/lib/state/authentication.reducer';
import { selectSocialMediaSharingStatus } from 'libs/shared/src/lib/states/client-local-state/client-local-state.selectors';


@Component({
  selector: 'rappider-live-preview',
  templateUrl: './live-preview.component.html',
  styleUrls: ['./live-preview.component.scss']
})
export class LivePreviewComponent implements OnInit, OnDestroy {

  @ViewChild('iframe', { static: false }) iframe: ElementRef;

  /* main title */
  mainTitle: HeadingComponentConfig = {
    content: 'Live Preview',
    type: defaultToolbarTitleHeadingSize
  };

  powerSwitchConfig: {
    tooltipText?: string,
    defaultValue?: boolean | undefined,
    loading?: boolean | undefined
  };
  powerButtonState: string;
  applicationState: string;

  /* breadcrumb title of the page */
  title: string[] | BreadcrumbOption[];
  subscriptions: Subscription[] = [];
  activeProject: ProjectWithRelations;
  backendURL: string;
  frontendURL: string;
  refreshIframeFlag = false;
  initialUpdateBreadcrumb = false;
  frontendURLWithoutTimeStamp: string;
  /**
   * The deployment status indicates whether the initiation of the deployment process
   * was successful or if an error occurred during its commencement;
   * it does not imply the completion or non-completion of the deployment itself.
  *
  * @type {DeployStatus}
  * @memberof LivePreviewComponent
  */
  deployStatus = DeployStatus.Failure;
  displayedBuild: any;
  displayedPreviousBuild: any;
  isDeploymentLogVisible = true;

  isIFrameContentLoaded = false;
  iframeVisibility = false;

  titleBarActionButtons = titleBarActionButtonsConfig;
  deployErrorAlert = deployErrorAlertConfig;
  projectDeploymentAnimation = projectDeploymentAnimation;
  timelineItems: TimelineItem[];
  projectVersions: ProjectVersion[];
  applicationInfo: any;
  previousTimelineProcesses: any;
  serverInfoAvailable = false;
  editorConfig = {
    theme: 'vs-dark',
    language: 'markdown',
    lineNumbers: { renderType: 0 },
    minimap: {
      enabled: false,
    }
  }

  gitActionWorkflowRunDetails: GitActionWorkflowRunDetailResponseDto;

  /**
   * timer of the git action workflow run details information getter
   *
   * @type {*}
   * @memberof LivePreviewComponent
   */
  getGitActionWorkflowRunDetailsTimer: any;

  /* enum definitions */
  DeployStatus = DeployStatus;

  /* third party libs */
  moment = moment;
  inProgressAnimationConfig = {
    path: 'assets/animations/angular-sdk-generating.json',
    loop: true
  };
  queuedAnimationConfig = {
    path: 'assets/animations/backend-exporting.json',
    loop: true
  };
  isThereSuccessfulDeploy: boolean;

  refreshFlag = false;
  private pollingDeploy$ = new Subject<void>();

  isSocialMediaShared = false;
  loading = true;

  constructor(
    private store: Store<{ deployManagement: DeployManagementState; activeProject: Project; projectVersion: ProjectVersion; auth: AuthState }>,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private syncRemoteDataService: SyncRemoteDataService
  ) { }

  ngOnInit(): void {
    this.subscribeToData();
    interval(5000).pipe(
      takeUntil(this.pollingDeploy$)
    ).subscribe(() => {
      this.syncRemoteDataService.syncRemoteDataItemWithKey(this.activeProject?.id, StoreKeys.DeployProject);
      this.syncRemoteDataService.syncRemoteDataItemWithKey(this.activeProject?.id, StoreKeys.MonitorApplication);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pollingDeploy$.next();
    this.pollingDeploy$.complete();
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToProjectVersions(),
      this.subscribeToApplicationInfo(),
      this.subscribeToDeployManagementInformation(),
      this.subscribeToDeploymentProcessStatus(),
      this.subscribeToActiveProject(),
      this.subscribeToPowerButtonState(),
      this.subscribeToPowerButtonPosition(),
      this.subscribeToPreferredThemeAndUpdateMonacoTheme(),
      this.subscribeToSocialMediaSharingStatus()
    ];
  }

  getLivePreviewInformation() {
    this.store.dispatch(GetLivePreviewInformation({ continuePollingAfterSuccessAndFailure: true }));
  }

  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject.data).subscribe(activeProject => {
      if (activeProject) {
        this.activeProject = activeProject;
        this.getLivePreviewInformation();
        this.setLivePreviewURLs(activeProject.id);
      }
      this.setPageTitle(activeProject);
    });
  }

  subscribeToDeploymentProcessStatus() {
    return this.store.select(state => state.deployManagement.deployInfo).subscribe((status: DeployStatus) => {
      this.deployStatus = status;
    });
  }

  subscribeToProjectVersions() {
    return this.store.select(state => state.projectVersion.data).subscribe((projectVersions: ProjectVersion[]) => {
      this.projectVersions = projectVersions;
    });
  }

  subscribeToApplicationInfo() {
    return this.store.select(state => state.deployManagement.applicationInfo).subscribe((applicationInfo: any) => {
      if (applicationInfo?.length) {
        this.serverInfoAvailable = true;
        if (applicationInfo[0].State === 'exited' || applicationInfo[1].State === 'exited') {
          this.powerSwitchConfig = {
            tooltipText: 'Power (on/off)',
            defaultValue: false
          };
        } else if (applicationInfo[0].State === 'running' && applicationInfo[1].State === 'running') {
          this.powerSwitchConfig = {
            tooltipText: 'Power (on/off)',
            defaultValue: true
          };
        } else {
          this.powerSwitchConfig = {
            tooltipText: 'Power (on/off)',
            defaultValue: undefined
          };
        }
        this.applicationState = (applicationInfo[0].State === 'running' && applicationInfo[1].State === 'running') ? 'running' : (applicationInfo[0].State === 'exited' || applicationInfo[1].State === 'exited') ? 'exited' : undefined;
        this.powerSwitchLoadingFunction();
      } else if (applicationInfo?.length === 0 || !applicationInfo) {
        this.serverInfoAvailable = false;
      }
    });
  }

  subscribeToPowerButtonState() {
    return this.store.select(state => state.deployManagement.applicationState).subscribe((state: string) => {
      this.powerButtonState = state;
      this.powerSwitchLoadingFunction();
    });
  }

  subscribeToPowerButtonPosition() {
    return this.store.select(state => state.deployManagement.switchPosition).subscribe((switchPosition) => {
      this.powerSwitchConfig = {
        tooltipText: 'Power (on/off)',
        defaultValue: switchPosition
      };
      this.powerSwitchLoadingFunction();
    });
  }

  subscribeToPreferredThemeAndUpdateMonacoTheme() {
    return this.store.select(state => state.auth?.activePerson?.preferredTheme).subscribe(preferredTheme => {
      if (preferredTheme === ThemeMode.Light) {
        this.editorConfig.theme = 'vs';
      } else {
        this.editorConfig.theme = MonacoEditorTheme.Dark;
      }
    });
  }

  subscribeToDeployManagementInformation() {
    return this.store.select(state => state.deployManagement?.deployInfo)
      .subscribe(deployInfo => {
        if (deployInfo?.length) {
          this.displayedBuild = {
            status: deployInfo[0]?.status,
            start_time: deployInfo[0]?.createdDate,
            end_time: this.displayedBuild?.end_time || null,
            version_branch: this.projectVersions?.find(projectVersion => projectVersion.id === deployInfo[0].projectVersionId)?.versionNumber,
            run_id: deployInfo[0]?.id
          }
          const lastSuccessfulVersion = deployInfo?.find((item, index) => index !== 0 && item.status === 'successful');
          this.displayedPreviousBuild = {
            status: lastSuccessfulVersion?.status,
            start_time: lastSuccessfulVersion?.createdDate,
            end_time: lastSuccessfulVersion?.updatedDate ? `${moment(lastSuccessfulVersion?.updatedDate).calendar()}` : undefined,
            version_branch: this.projectVersions?.find(projectVersion => projectVersion.id === lastSuccessfulVersion?.projectVersionId)?.versionNumber,
            run_id: lastSuccessfulVersion?.id
          }

          // In this case, UI manipulation was performed, and if the final step is successful, all steps are considered successful. If the final step fails, all steps are considered failed.
          if (!isEqual(this.previousTimelineProcesses, deployInfo[0])) {
            if (deployInfo[0].status === 'successful') {
              this.timelineItems = deployInfo[0].processes.map(item => {
                const latestItem = deployInfo[0].processes?.reduce((a, b) =>
                  a.timestamp > b.timestamp ? a : b
                );
                if (item === latestItem) {
                  this.displayedBuild = {
                    ...this.displayedBuild,
                    end_time: latestItem.timestamp
                  };
                  return {
                    ...item, title: item.message,
                    icon: {
                      name: 'fa-solid fa-circle-check',
                      type: IconType.FontAwesome,
                      color: 'var(--success-color)',
                    },
                    date: item.timestamp,
                    color: TimelineItemColor.Success
                  };
                } else {
                  return {
                    ...item, title: item.message,
                    icon: {
                      name: 'fa-regular fa-info-circle',
                      type: IconType.FontAwesome,
                      color: 'var(--text-color)',
                      size: 'var(--margin-4x)'
                    },
                    date: item.timestamp,
                    color: TimelineItemColor.Success
                  };
                }
              });
            } else if (deployInfo[0].status === 'failure') {
              this.timelineItems = deployInfo[0].processes.map(item => {
                const latestItem = deployInfo[0].processes?.reduce((a, b) =>
                  a.timestamp > b.timestamp ? a : b
                );
                if (item === latestItem) {
                  this.displayedBuild = {
                    ...this.displayedBuild,
                    end_time: latestItem.timestamp
                  };
                  return {
                    ...item, title: item.message,
                    icon: {
                      name: 'fa-regular fa-xmark',
                      type: IconType.FontAwesome,
                      color: 'var(--danger-color)',
                    },
                    date: item.timestamp,
                    color: TimelineItemColor.Success
                  };
                } else {
                  return {
                    ...item, title: item.message,
                    icon: {
                      name: 'fa-regular fa-info-circle',
                      type: IconType.FontAwesome,
                      color: 'var(--text-color)',
                      size: 'var(--margin-4x)'
                    },
                    date: item.timestamp,
                    color: TimelineItemColor.Success
                  };
                }
              });
            } else if (deployInfo[0].status === 'queued') {
              this.displayedBuild = {
                ...this.displayedBuild,
                end_time: null
              };
              this.timelineItems = [{
                title: 'You are currently in the queue. Your request will be processed as soon as it reaches the front.',
                icon: {
                  name: 'fa-duotone fa-gear fa-spin',
                  type: IconType.FontAwesome,
                  color: 'var(--text-color)'
                },
                date: deployInfo[0].status.createdDate,
                color: TimelineItemColor.Success
              }];
            } else if (deployInfo[0].status === 'in-progress') {
              this.timelineItems = deployInfo[0].processes.map(item => {
                const latestItem = deployInfo[0].processes?.reduce((a, b) =>
                  a.timestamp > b.timestamp ? a : b
                );
                if (item === latestItem) {
                  this.displayedBuild = {
                    ...this.displayedBuild,
                    end_time: null
                  };
                  return {
                    ...item, title: item.message,
                    icon: {
                      name: 'fa-duotone fa-gear fa-spin',
                      type: IconType.FontAwesome,
                      color: 'var(--text-color)',
                    },
                    date: item.timestamp,
                    color: TimelineItemColor.Success
                  };
                } else {
                  return {
                    ...item, title: item.message,
                    icon: null,
                    date: item.timestamp,
                    color: null
                  };
                }
              });
            } else {
              this.timelineItems = deployInfo[0].processes.map(item => {
                const latestItem = deployInfo[0].processes?.reduce((a, b) =>
                  a.timestamp > b.timestamp ? a : b
                );
                if (item === latestItem) {
                  return {
                    ...item, title: item.message,
                    icon: {
                      name: item.status === 'in-progress' ? 'fa-duotone fa-gear fa-spin'
                        : item.status === 'failure' ? 'fa-regular fa-xmark'
                          : item.status === 'successful' ? 'fa-solid fa-circle-check'
                            : 'fa-solid fa-circle-check',
                      type: IconType.FontAwesome,
                      color: item.status === 'in-progress' ? 'var(--text-color)'
                        : item.status === 'failure' ? 'var(--danger-color)'
                          : item.status === 'successful' ? 'var(--success-color)'
                            : 'var(--success-color)',
                    },
                    date: item.timestamp,
                    color: TimelineItemColor.Success
                  };
                } else {
                  return {
                    ...item, title: item.message,
                    icon: null,
                    date: item.timestamp,
                    color: null
                  };
                }
              });
            }
            // this setTimeout waiting array.find() function
            setTimeout(() => {
              this.updateBreadcrumb();
            }, 0);
          }
          this.isThereSuccessfulDeploy = deployInfo.some(obj =>
            obj.processes.some(process => process.status === "successful")
          );
          this.previousTimelineProcesses = deployInfo[0];
        } else if (deployInfo !== null) {
          this.displayedBuild = {
            status: DeployStatus.NotStarted
          }
        }

      });
  }

  subscribeToSocialMediaSharingStatus() {
    const loadingTimeout = setTimeout(() => {
      this.loading = false;
    }, 3000);

    return this.store.select(selectSocialMediaSharingStatus).subscribe({
      next: (isShared: boolean) => {
        clearTimeout(loadingTimeout);

        this.isSocialMediaShared = isShared;
        this.loading = false;
      },
      error: (error) => {
        clearTimeout(loadingTimeout);
        this.isSocialMediaShared = false;
        this.loading = false;
      }
    });
  }

  updateBreadcrumb() {
    if (!(this.displayedBuild.status === DeployStatus.Successful)) {
      /* Update Breadcrumb */
      if (!(this.displayedBuild?.status === DeployStatus.Successful)) {
        this.title[2] = {
          label: this.displayedPreviousBuild?.end_time ? `Previewing the build on ${this.displayedPreviousBuild?.end_time}` : 'No successful deployment found',
        };
      }
      this.initialUpdateBreadcrumb = true;
    } else {
      if (this.displayedBuild.version_branch) {
        // update breadcrumb
        this.title[2] = {
          label: this.displayedBuild?.version_branch,
        };
        // update url
        const frontendURL = new URL(`https://${this.activeProject.id}.app.rapider.ai`);
        this.frontendURLWithoutTimeStamp = frontendURL.href;
        frontendURL.searchParams.append('timestamp', new Date().getTime().toString());
        this.frontendURL = frontendURL.href;

        // Update iframe src after 5 seconds
        setTimeout(() => {
          // src is updated to refresh iframe
          this.iframe.nativeElement.src = this.frontendURL;
        }, 5000);

        // We set it to true to avoid it from refreshing repeatedly.
        this.refreshIframeFlag = true;

        this.cdr.detectChanges();
      }
    }
  }

  onIFrameContentLoaded() {
    this.isIFrameContentLoaded = true;
    this.cdr.detectChanges();
    // this.getIFrameStatus();
  }

  getIFrameStatus() {
    if (this.iframe) {
      const headers = new HttpHeaders({
        'Access-Control-Allow-Origin': '*',
      });

      this.http.get(this.frontendURLWithoutTimeStamp,
        { headers: headers, withCredentials: true }).pipe(
          catchError(error => {
            console.log(error);
            return throwError(error);
          })
        ).subscribe((response: any) => {
          console.log(response);
        });
    } else {
      console.log('Iframe not ready yet, waiting...');
    }
  }

  setPageTitle(activeProject: ProjectWithRelations) {
    this.title = [
      {
        label: activeProject?.name,
        redirectUrl: `${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${activeProject?.id}`
      },
      {
        label: 'Live Preview',
      }
    ];
  }

  toggleDeploymentLogVisibility() {
    this.isDeploymentLogVisible = !this.isDeploymentLogVisible;
  }

  onTitleBarActionButtonClick(action) {
    if (action.key === 'deploymentLog') {
      const deploymentButton = this.titleBarActionButtons.find(button => button.key === 'deploymentLog');
      deploymentButton.icon = {
        ...deploymentButton.icon,
        name: this.isDeploymentLogVisible ? 'menu-fold' : 'menu-unfold'
      };
      this.toggleDeploymentLogVisibility();
    } else if (action.key === 'openInNewTab') {
      window.open(this.frontendURL, '_blank');
    }
  }

  setLivePreviewURLs(activeProjectId: string) {
    /* set timestamp */
    const timestamp = new Date().getTime().toString();

    /* define as url object */
    const frontendURL = new URL(`https://${activeProjectId}.app.rapider.ai`);
    this.frontendURLWithoutTimeStamp = frontendURL.href;
    /* set timestamp query param */
    frontendURL.searchParams.append('timestamp', new Date().getTime().toString());
    /* set frontend url */
    this.frontendURL = frontendURL.href;

    /* define as url object */
    const backendURL = new URL(`https://${activeProjectId}.api.rapider.ai`);
    /* set timestamp query param */
    backendURL.searchParams.append('timestamp', timestamp);
    /* set backend url */
    this.backendURL = backendURL.href;
  }

  setDeployStatusIcon(livePreviewStatus) {
    const defaultIcon = 'fa-duotone fa-gear fa-spin';
    let icon: string;
    let text: string;

    if (livePreviewStatus === DeployStatus.Successful) {
      icon = 'fa-duotone fa-check';
      text = 'Completed';
    } else if (livePreviewStatus === DeployStatus.InProgress) {
      icon = defaultIcon;
      text = 'In Progress';
    } else if (livePreviewStatus === DeployStatus.Failure) {
      icon = 'fa-duotone fa-x';
      text = 'Failed';
    } else if (livePreviewStatus === DeployStatus.NotStarted) {
      icon = 'fa-regular fa-hexagon-exclamation';
      text = 'Not Started';
    } else {
      icon = defaultIcon;
      text = 'Deploying';
    }

    return { icon, text };
  }

  onSwitchToggled(switchValue) {
    if (switchValue) {
      this.store.dispatch(StartApplication());
    } else {
      this.store.dispatch(StopApplication());
    }
  }

  powerSwitchLoadingFunction() {
    this.powerSwitchConfig = {
      ...this.powerSwitchConfig,
      loading: (this.powerButtonState !== this.applicationState)
    };
  }

}
